import {
	Callout,
	Checkbox,
	DefaultButton,
	DirectionalHint,
	IColumn,
	IconButton,
	IDetailsColumnRenderTooltipProps,
	IDetailsHeaderProps,
	IRenderFunction,
	List,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	ScrollablePane,
	ScrollbarVisibility,
	SearchBox,
	Stack,
	Sticky
} from "@fluentui/react"
import { useEffect, useState } from "react"
import { getFieldDisplayValue, getObjectValue, safeFormatDateValue, toBoolean } from "../../lib/helper"

export enum FieldType {
	Text = "Text",
	RichText = "RichText",
	Number = "Number",
	Email = "Email",
	Date = "Date",
	DateTime = "DateTime",
	Boolean = "Boolean",
	User = "User",
	Currency = "Currency",
	Custom = "Custom"
}

export interface IColumnData {
	filterable: boolean
	sortable: boolean
	type: FieldType
	filterValue: (item: any) => string
}

export interface IFilterTag {
	text: string
	description: string
	onClick?: () => void
}

export interface IColumnSortKeyAndDirection {
	key: string
	isDescending: boolean
}

export interface IFieldFilter {
	filterField: IColumn
	filterValues: string[]
}

interface IFieldFilterValues {
	FieldDisplayName: string
	Type: FieldType
	Values: any[]
}

export interface IFieldFilters {
	[x: string]: IFieldFilterValues
}

interface IDetailsListFilterCalloutProps {
	FilterField: IFieldFilter
	ItemResults: any[]
	GetObjectValue: (object: any, key: string, isSalesforceField?: boolean) => string
	OnDismiss: () => void
	OnClearFilter: (key: string) => void
	OnSaveFilter: (values: string[]) => void
}

const _onFilterClick = (column: IColumn, setColumnFilterEnabled): void | undefined => {
	setColumnFilterEnabled(column)
}

const _onClearFilter = (key: string, currentFilters, setCurrentFilters, setColumnFilterEnabled) => {
	const currentFieldFilters = currentFilters
	delete currentFieldFilters[key] //currentFieldFilters![key]
	setCurrentFilters({ ...currentFieldFilters })
	setColumnFilterEnabled(undefined)
}

const _shouldFilterIconShow = (column: IColumn, items, currentFilters) => {
	const options = items
		.map(deal => {
			return getObjectValue(deal, column.key)
		})
		.filter((n, i, array) => array.indexOf(n) === i)
	return options.length > 1 || Object.keys(currentFilters).find(k => k === column.key) !== undefined
}

export const filterDataByColumnFilters = (item: any, filterTags: IFilterTag[], currentFilters: IFieldFilters, getColumns: () => IColumn[], onClearFilter: (key: string) => void): boolean => {
	const keys = Object.keys(currentFilters)
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i]
		const columnFilter = currentFilters[key]
		const column = getColumns().find(c => c.key === key)
		const columnData: IColumnData = column.data as IColumnData
		let filterTagValues = ""
		const includeComma = columnFilter.Values.length > 2

		columnFilter.Values.forEach((v, vi) => {
			if (vi > 0 && includeComma) {
				filterTagValues += ","
			}
			if (columnFilter.Values.length > 1 && vi + 1 === columnFilter.Values.length) {
				filterTagValues += " or"
			}
			filterTagValues += ` ${v}`
		})

		if (!filterTags.some(f => f.text === `Column: ${columnFilter.FieldDisplayName}`)) {
			filterTags.push({
				text: `Column: ${columnFilter.FieldDisplayName}`,
				description: `Only show deals with a ${key} of${filterTagValues}.`,
				onClick: () => {
					onClearFilter(key)
				}
			})
		}

		const value = getObjectValue(item, key, columnFilter.Type)
		if (columnFilter.Type === FieldType.Custom) {
			const value = columnData.filterValue(item)
			if (!columnFilter.Values.includes(value)) {
				return false
			}
		} else if (!columnFilter.Values.includes(value)) {
			return false
		}
	}
	return true
}

export const renderCustomListHeader = (
	columnFilterEnabled: IColumn,
	setColumnFilterEnabled: React.Dispatch<React.SetStateAction<IColumn>>,
	items: any[],
	currentFilters: IFieldFilters,
	setCurrentFilters: React.Dispatch<React.SetStateAction<IFieldFilters>>,
	sortColumn: IColumnSortKeyAndDirection,
	setSortColumn: React.Dispatch<React.SetStateAction<IColumnSortKeyAndDirection>>,
	detailsHeaderProps?: IDetailsHeaderProps,
	defaultRender?: IRenderFunction<IDetailsHeaderProps>
) => {
	return (
		<>
			<Sticky isScrollSynced>
				{detailsHeaderProps &&
					defaultRender &&
					defaultRender({
						...detailsHeaderProps,
						onRenderColumnHeaderTooltip: (tooltipHostProps: IDetailsColumnRenderTooltipProps | undefined) => {
							const column = tooltipHostProps?.column
							if (column) {
								const hoverSortIconStyle = {
									root: { selectors: { "& .ms-DetailsHeader-cellTitle": { paddingLeft: 0 }, ":hover .sortIconCell, :hover .filterIconCell": { display: "inline-block !important" } } }
								}
								const currentFilter = columnFilterEnabled?.key === column.key
								const columnSortable = column.data?.sortable && items.length > 1
								const columnFilterable = column.data?.filterable && _shouldFilterIconShow(column, items, currentFilters)
								const isCurrentColumnFiltered = Object.keys(currentFilters).includes(column.key)
								if (columnFilterable || columnSortable) {
									return (
										<Stack horizontal styles={hoverSortIconStyle}>
											<>{tooltipHostProps?.children}</>
											{columnSortable && (
												<IconButton
													data-automation-id={"sort-by-" + column.key}
													iconProps={{ iconName: sortColumn?.key === column.key ? (sortColumn.isDescending ? "SortDown" : "SortUp") : "Sort" }}
													onClick={() => {
														setSortColumn({
															key: column.key,
															isDescending: sortColumn.key === column.key ? !sortColumn.isDescending : false
														})
													}}
													className="sortIconCell"
													style={{ display: "none" }}
													styles={{ root: { width: 24, height: 40 } }}
												/>
											)}
											{columnFilterable && !currentFilter && !isCurrentColumnFiltered && (
												<IconButton
													data-automation-id={"filter-by-" + column.key}
													iconProps={{ iconName: "Filter" }}
													onClick={() => {
														_onFilterClick(column, setColumnFilterEnabled)
													}}
													className="filterIconCell"
													style={{ display: "none" }}
													styles={{ root: { width: 24, height: 40 } }}
												/>
											)}
											{columnFilterable && isCurrentColumnFiltered && (
												<IconButton
													data-automation-id={"active-filter-" + column.key}
													iconProps={{ iconName: "FilterSolid" }}
													onClick={() => {
														_onFilterClick(column, setColumnFilterEnabled)
													}}
													className=""
													styles={{ root: { width: 24, height: 40 } }}
												/>
											)}
											{columnFilterable && isCurrentColumnFiltered && (
												<IconButton
													data-automation-id={"clear-filter-" + column.key}
													iconProps={{ iconName: "ClearFilter" }}
													onClick={() => {
														_onClearFilter(column.key, currentFilters, setCurrentFilters, setColumnFilterEnabled)
													}}
													className=""
													styles={{ root: { width: 24, height: 40 } }}
												/>
											)}
										</Stack>
									)
								}
							}
							return <span title={column?.name}>{tooltipHostProps?.children}</span>
						}
					})}
			</Sticky>
			{columnFilterEnabled && (
				<DetailsListFilterCallout
					ItemResults={items}
					GetObjectValue={(item: any, key: string) => {
						const val = item[key]
						return val
					}}
					FilterField={{ filterField: columnFilterEnabled, filterValues: currentFilters[columnFilterEnabled.key] ? currentFilters[columnFilterEnabled.key].Values : [] }}
					OnDismiss={() => {
						setColumnFilterEnabled(undefined)
					}}
					OnClearFilter={(key: string) => {
						const currentFieldFilters = currentFilters
						delete currentFieldFilters[key] //currentFieldFilters![key]
						setCurrentFilters({ ...currentFieldFilters })
						setColumnFilterEnabled(undefined)
					}}
					OnSaveFilter={(values: string[]) => {
						const currentFieldFilters = currentFilters || {}
						if (currentFieldFilters) {
							currentFieldFilters[columnFilterEnabled?.key] = {
								Values: values,
								Type: columnFilterEnabled?.data.type,
								FieldDisplayName: columnFilterEnabled?.name
							}
						}
						setColumnFilterEnabled(undefined)
						setCurrentFilters({ ...currentFieldFilters })
					}}
				/>
			)}
		</>
	)
}

export function DetailsListFilterCallout(props: IDetailsListFilterCalloutProps): JSX.Element {
	const [filterOptions, setFilterOptions] = useState<any[]>([])
	const [filterOptionsFiltered, setFilterOptionsFiltered] = useState<any[]>([])
	const [selectedFilterOptions, setSelectedFilterOptions] = useState<any[]>(props.FilterField.filterValues)
	const [error, setError] = useState<string | undefined>()

	useEffect(() => {
		const options = []
		const column = props.FilterField.filterField
		const columnData: IColumnData = column.data as IColumnData
		let reverseSort = false
		let valueProcessor: (v: any) => string

		switch (columnData?.type) {
			case FieldType.Boolean: {
				valueProcessor = v => (toBoolean(v) ? "Yes" : "No")
				break
			}
			case FieldType.Date:
			case FieldType.DateTime: {
				reverseSort = true
				valueProcessor = v => {
					return safeFormatDateValue(v, "MM/dd/yyyy", "")
				}
				break
			}
			case FieldType.Custom: {
				valueProcessor = v => {
					return columnData.filterValue(v)
				}
				break
			}
		}

		for (const group of props.ItemResults) {
			let value = props.GetObjectValue(group, column.key)
			if (column.data?.type === FieldType.Custom) {
				value = group
			}
			if (typeof valueProcessor === "function") {
				try {
					value = valueProcessor(value)
				} catch (err) {
					console.error("Error processing value.", err)
					value = ""
				}
			}
			if (value && !options.includes(value)) {
				options.push(value)
			}
		}

		options.sort((a, b) => {
			const aVal = a || ""
			const bVal = b || ""
			return (aVal < bVal ? -1 : 1) * (reverseSort ? -1 : 1)
		})

		setFilterOptions(options)
		setFilterOptionsFiltered(options)
	}, [props, props.FilterField, props.ItemResults, props.GetObjectValue])

	const onSearchFilterChanged = (ev?: any, text?: string): void => {
		setFilterOptionsFiltered(text ? filterOptions.filter(fo => fo && fo.toLowerCase().indexOf(text.toLowerCase()) >= 0) : filterOptions)
	}

	const headerClass = `FilterableHeader-${props.FilterField.filterField.key}`
	const key = `filterCallout-${props.FilterField.filterField.key}`

	return (
		<Callout key={key} directionalHint={DirectionalHint.bottomCenter} isBeakVisible={false} hidden={false} onDismiss={props.OnDismiss} target={`.${headerClass}`}>
			<div style={{ padding: 16 }} data-automation-id={key}>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				<Stack tokens={{ childrenGap: 10 }}>
					<span style={{ fontWeight: 500 }}>Filter On: {props.FilterField.filterField.name}</span>
					<Stack tokens={{ childrenGap: 4 }}>
						<SearchBox placeholder="Search filters" iconProps={{ iconName: "Filter" }} onChange={onSearchFilterChanged} />
						<div style={{ position: "relative", height: 300 }}>
							<ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
								<List
									items={filterOptionsFiltered.slice()}
									onRenderCell={(f?: any, index?: number) => {
										const label = getFieldDisplayValue({
											Value: f,
											Type: props.FilterField.filterField.data?.type !== FieldType.User ? props.FilterField.filterField.data?.type : FieldType.Text
										})
										return (
											f !== undefined && (
												<Checkbox
													styles={{ root: { paddingTop: index && index > 0 ? 10 : 0 } }}
													key={`checkbox-filter-${f}`}
													label={label}
													checked={selectedFilterOptions.includes(f)}
													onChange={(_ev, checked) => {
														let updatedSelectedFilterOptions = [...selectedFilterOptions]
														if (checked && !updatedSelectedFilterOptions.includes(f)) {
															updatedSelectedFilterOptions.push(f)
														} else {
															updatedSelectedFilterOptions = updatedSelectedFilterOptions.filter(sfo => sfo !== f)
														}
														setSelectedFilterOptions(updatedSelectedFilterOptions)
													}}
												/>
											)
										)
									}}
								/>
							</ScrollablePane>
						</div>
					</Stack>
				</Stack>
				<Stack style={{ paddingTop: 8 }} horizontal tokens={{ childrenGap: 10 }}>
					<PrimaryButton
						data-automation-id="filterCallout-applyFilter"
						text="Apply"
						disabled={selectedFilterOptions.length === 0}
						onClick={() => props.OnSaveFilter(selectedFilterOptions)}
					/>
					<DefaultButton
						data-automation-id="filterCallout-clearFilter"
						text="Clear Filter"
						onClick={() => {
							setSelectedFilterOptions([])
							const column = props.FilterField.filterField
							props.OnClearFilter(column.key)
						}}
					/>
					<DefaultButton data-automation-id="filterCallout-cancel" text="Cancel" onClick={props.OnDismiss} />
				</Stack>
			</div>
		</Callout>
	)
}

export default DetailsListFilterCallout
